'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { getMenuVisibility } from '@/lib/services/rbacService';
import { isLuxePlan } from '@/lib/constants/planLevel';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  isCollapsed?: boolean;
  /** When sidebar is shown as mobile overlay, call this when user navigates or closes */
  onClose?: () => void;
  /** True when rendered inside the mobile overlay (show close button, full width) */
  isMobileOverlay?: boolean;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  badge?: number;
  visibility: 'standard' | 'founder' | 'admin';
  requiresPro?: boolean;
  /** Only show for Luxe users (current premium); hidden for Basic */
  requiresLuxe?: boolean;
  /** Hide specifically for Luxe users to prevent manual upload duplication */
  hideForLuxe?: boolean;
  /** Optional font size for label (default 14). Use 13 for compact items. */
  fontSize?: number;
}

const MOCK_PLAN_PARAM = 'mock_plan=Luxe';

const Sidebar = ({ isCollapsed = false, onClose, isMobileOverlay = false }: SidebarProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, signOut } = useAuth();
  const mockLuxe = pathname === '/luxe-mock' || searchParams.get('mock_plan') === 'Luxe';
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [menuVisibility, setMenuVisibility] = useState({
    showStandardMenu: true,
    showFounderMenu: false,
    showAdminSettings: false,
    isMasterAdmin: false
  });
  const [userPlan, setUserPlan] = useState<'Basic' | 'Luxe' | 'Pro' | 'Enterprise'>('Basic');
  const [userProfile, setUserProfile] = useState<{ full_name: string; email: string; profile_picture_url?: string } | null>(null);
  const [planLabelLoading, setPlanLabelLoading] = useState(true);
  const [planRefreshTrigger, setPlanRefreshTrigger] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const supabase = createClient();

  // Display label from plan_id: exactly Luxe (case-insensitive) -> "Luxe Agent", else "Agent". Default "Agent" while loading.
  const planDisplayLabel =
    planLabelLoading ? 'Agent' : (String(userPlan).toLowerCase() === 'luxe' ? 'Luxe Agent' : 'Agent');

  useEffect(() => {
    const fetchMenuVisibility = async () => {
      const visibility = await getMenuVisibility();
      setMenuVisibility(visibility);
    };
    fetchMenuVisibility();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setPlanLabelLoading(false);
        return;
      }
      setPlanLabelLoading(true);
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('plan_id, full_name, profile_picture_url, luxe_override, free_until_date, lifetime_access')
          .eq('id', user.id)
          .single();

        const displayName = profile?.full_name?.trim() || user.user_metadata?.full_name || user.email || 'User';
        const displayEmail = user.email || '';

        if (profile) {
          const hasOverride = !!(
            profile.luxe_override ||
            profile.lifetime_access ||
            (profile.free_until_date && new Date(profile.free_until_date) > new Date())
          );
          const rawPlan = (profile.plan_id as 'Basic' | 'Luxe' | 'Pro' | 'Enterprise') || 'Basic';
          setUserPlan(mockLuxe ? 'Luxe' : (hasOverride ? 'Luxe' : rawPlan));
        } else {
          setUserPlan(mockLuxe ? 'Luxe' : 'Basic');
        }
        setUserProfile({
          full_name: displayName,
          email: displayEmail,
          profile_picture_url: profile?.profile_picture_url ?? undefined
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUserPlan(mockLuxe ? 'Luxe' : 'Basic');
        setUserProfile({
          full_name: user.user_metadata?.full_name || user.email || 'User',
          email: user.email || '',
          profile_picture_url: undefined
        });
      } finally {
        setPlanLabelLoading(false);
      }
    };
    fetchUserData();
  }, [user, planRefreshTrigger, mockLuxe]);

  // Refetch plan when user returns to tab or after onboarding completes (so Luxe shows correctly)
  useEffect(() => {
    const onRefresh = () => setPlanRefreshTrigger((t) => t + 1);
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') onRefresh();
    };
    window.addEventListener('tally-plan-updated', onRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('tally-plan-updated', onRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // After login: sync plan from Stripe. Only mark "synced" on success so we retry on next load if sync failed or returned Basic.
  useEffect(() => {
    if (!user || planLabelLoading) return;
    const key = `tally_plan_synced_${user.id}`;
    if (typeof window === 'undefined' || sessionStorage.getItem(key)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/stripe/sync-my-plan', { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success) {
          if (typeof window !== 'undefined') sessionStorage.setItem(key, '1');
          if (data.plan_id === 'Luxe') window.location.reload();
        }
      } catch {
        // Don't set key so we retry on next load
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, planLabelLoading]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      if (typeof window !== 'undefined' && user) sessionStorage.removeItem(`tally_plan_synced_${user.id}`);
      await signOut();
      router.push('/login-authentication');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  // 1. MENU Items
  const menuNavItems: NavItem[] = [
    { label: 'Dashboard', path: '/main-commission-dashboard', icon: 'ChartBarIcon', visibility: 'standard' },
    { label: 'Your Earnings Watch', path: '/smart-ledger-analytics', icon: 'DocumentTextIcon', visibility: 'standard' },
  ];

  // 2. FINANCIAL Items , Carrier Connections first, then Payment Match, Revenue Recovery, Reports
  const financialNavItems: NavItem[] = [
    { label: 'Carrier Connections', path: '/carrier-connections-management', icon: 'LinkIcon', visibility: 'standard', fontSize: 13, requiresLuxe: true },
    { label: 'Payment Match', path: '/payment-match', icon: 'ScaleIcon', visibility: 'standard' },
    { label: 'Revenue Recovery', path: '/dispute-resolution-tracking', icon: 'ExclamationCircleIcon', visibility: 'standard' },
    { label: 'Reports', path: '/reports', icon: 'DocumentChartBarIcon', visibility: 'standard', requiresLuxe: true },
  ];

  // 3. TOOLS Items
  const toolsNavItems: NavItem[] = [
    { label: 'Client Enrollment Log', path: '/client-enrollment', icon: 'UserPlusIcon', visibility: 'standard' },
    { label: 'Commission Tracker', path: '/launch-data-audit', icon: 'DocumentArrowUpIcon', visibility: 'standard' },
    { label: 'Settings', path: '/user-profile-settings', icon: 'Cog6ToothIcon', visibility: 'standard' },
  ];

  // Basic plan: single flat list , Payment Match before Revenue Recovery for flow
  const basicPlanNavItems: NavItem[] = [
    { label: 'Dashboard', path: '/main-commission-dashboard', icon: 'ChartBarIcon', visibility: 'standard' },
    { label: 'Your Earnings Watch', path: '/smart-ledger-analytics', icon: 'DocumentTextIcon', visibility: 'standard' },
    { label: 'Client Enrollment Log', path: '/client-enrollment', icon: 'UserPlusIcon', visibility: 'standard' },
    { label: 'Commission Tracker', path: '/launch-data-audit', icon: 'DocumentArrowUpIcon', visibility: 'standard' },
    { label: 'Payment Match', path: '/payment-match', icon: 'ScaleIcon', visibility: 'standard' },
    { label: 'Revenue Recovery', path: '/dispute-resolution-tracking', icon: 'ExclamationCircleIcon', visibility: 'standard' },
    { label: 'Settings', path: '/user-profile-settings', icon: 'Cog6ToothIcon', visibility: 'standard' },
  ];

  /* REFINED FILTERING LOGIC:
     - If an item requiresLuxe and the user isn't Luxe, hide it.
     - If an item is hideForLuxe and the user IS Luxe, hide it.
  */
  const isLuxe = isLuxePlan(userPlan);

  const filterItems = (items: NavItem[]) => items.filter(item => {
    if (item.requiresLuxe && !isLuxe) return false;
    if (item.hideForLuxe && isLuxe) return false;
    return true;
  });

  const visibleMenuItems = filterItems(menuNavItems);
  const visibleFinancialItems = filterItems(financialNavItems);
  const visibleToolsItems = filterItems(toolsNavItems);

  const isActive = (path: string) => pathname === path;
  const sectionLabelColor = '#c5c9cd';
  const navTextColor = '#111827';
  const activeBg = '#236434';
  const activeText = '#FFFFFF';

  const handleLockedItemClick = (e: React.MouseEvent, item: NavItem) => {
    if (item.requiresPro && userPlan === 'Basic') {
      e.preventDefault();
      window.location.href = item.path;
    }
  };

  const hrefFor = (path: string) =>
    mockLuxe ? `${path}${path.includes('?') ? '&' : '?'}${MOCK_PLAN_PARAM}` : path;

  const renderNavItem = (item: NavItem) => {
    const isLocked = item.requiresPro && userPlan === 'Basic';
    const active = isActive(item.path);
    const itemContent = (
      <>
        <Icon
          name={item.icon as any}
          size={22}
          strokeWidth={1.5}
          className={`flex-shrink-0 ${active ? 'text-[#FFFFFF]' : 'text-[#111827] group-hover:opacity-80'} ${isLocked ? 'opacity-60' : ''}`}
        />
        {!collapsed && (
          <span
            className="flex items-center shrink-0 min-w-0 overflow-visible"
            style={{
              opacity: isLocked ? 0.6 : 1,
              marginLeft: 0,
              position: 'relative',
              color: active ? '#FFFFFF' : navTextColor,
              fontSize: `${item.fontSize ?? 14}px`,
              fontWeight: active ? 600 : 500,
            }}
          >
            {item.label}
          </span>
        )}
      </>
    );

    return (
      <li key={item.path}>
        <Link
          href={hrefFor(item.path)}
          onClick={onClose}
          className={`flex items-center gap-1 px-3 py-[6px] transition-smooth group rounded-[6px] ${
            active ? 'bg-[#236434] text-white' : 'hover:bg-black/5'
          }`}
          style={active ? undefined : { color: navTextColor }}
        >
          {itemContent}
        </Link>
      </li>
    );
  };

  return (
    <aside
      className={`z-40 transition-smooth rounded-[6px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] h-full min-h-[calc(100vh-2rem)] ${isMobileOverlay ? 'w-full' : collapsed ? 'w-20' : 'w-[260px]'}`}
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="flex flex-col h-full w-full rounded-[6px] overflow-hidden">
        <div className={`flex items-center pt-[44px] pb-6 ${collapsed && !isMobileOverlay ? 'justify-center px-2' : 'justify-between px-5'}`}>
          {!collapsed && (
            <Link href={hrefFor('/main-commission-dashboard')} className="flex items-center min-w-0 flex-1" onClick={onClose}>
              <h1 className="flex items-center" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '1.4057125rem', color: '#111827' }}>
                <span style={{ fontWeight: 700 }}>Tally</span><span style={{ fontWeight: 400 }}>Medicare</span>
              </h1>
            </Link>
          )}
          {isMobileOverlay && onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-[6px] hover:bg-black/5 text-[#111827] transition-colors"
              aria-label="Close menu"
            >
              <Icon name="XMarkIcon" size={22} strokeWidth={1.8} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-[6px] hover:bg-black/5 text-[#111827] transition-colors"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={22} strokeWidth={1.8} />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 min-h-0">
          <ul className="flex flex-col gap-[14px]">
            {!isLuxe ? (
              basicPlanNavItems.map(renderNavItem)
            ) : (
              <>
                {!collapsed && <li className="px-3 py-[6px] uppercase tracking-wider text-[12px] font-700" style={{ color: sectionLabelColor }}>MENU</li>}
                {visibleMenuItems.map(renderNavItem)}

                {!collapsed && <li className="px-3 py-[6px] pt-[32px] uppercase tracking-wider text-[12px] font-700" style={{ color: sectionLabelColor }}>FINANCIAL</li>}
                {visibleFinancialItems.map(renderNavItem)}

                {visibleToolsItems.length > 0 && (
                  <>
                    {!collapsed && <li className="px-3 py-[6px] pt-[32px] uppercase tracking-wider text-[12px] font-700" style={{ color: sectionLabelColor }}>TOOLS</li>}
                    {visibleToolsItems.map(renderNavItem)}
                  </>
                )}
              </>
            )}
          </ul>
        </nav>

        <div className="px-3 py-3 space-y-2 mt-auto">
          {!collapsed && !isLuxe && (
            <div
              className="flex flex-col rounded-[6px] p-4 text-left w-full"
              style={{ backgroundColor: '#1a1a1a' }}
            >
              <p className="font-bold text-white text-[15px] mb-2">Upgrade Luxe</p>
              <p className="text-white/90 text-[13px] leading-snug mb-4">
                Get Analytics, Reports, and Carrier Auto-Sync with an upgraded account.
              </p>
              <Link
                href="/pricing"
                className="inline-flex items-center justify-center w-full py-2 rounded-[6px] font-semibold text-[13px] transition-opacity hover:opacity-90 mb-2"
                style={{ backgroundColor: '#ffe889', color: '#1a1a1a' }}
              >
                Upgrade
              </Link>
              <Link
                href="/verify-subscription"
                className="text-white/70 hover:text-white text-[12px] underline"
              >
                Already paid? Verify subscription
              </Link>
            </div>
          )}
          <div className="flex items-center gap-1 px-3 py-2 rounded-[6px] bg-black/5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-[#1a2e25]">
              {userProfile?.profile_picture_url?.trim() ? (
                <Image
                  src={userProfile.profile_picture_url}
                  alt=""
                  width={64}
                  height={64}
                  className="w-full h-full object-cover"
                  unoptimized
                  priority
                  sizes="32px"
                />
              ) : (
                <Icon name="UserCircleIcon" size={35} className="text-white/80" />
              )}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="truncate font-500 text-[14px] text-[#111827]">{userProfile?.full_name || 'User'}</p>
                <p className="truncate uppercase font-medium text-[11px] text-slate-500">{planDisplayLabel}</p>
              </div>
            )}
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-1 px-3 py-2 rounded-[6px] hover:bg-black/5 text-[#111827]">
            <Icon name="ArrowRightOnRectangleIcon" size={22} />
            {!collapsed && <span className="text-[14px] font-500">Logout</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;